import { CalendarX, Clock, EyeOff, Heart, MapPin, Medal, MoreVertical, Pencil, Play, Sofa, Users, Zap } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Pill } from '../../components/ui/Pill';
import { getMainCategory } from '../../domain/activities';
import type { Activity } from '../../domain/types';
import { usePrefs, useToast } from '../../state/AppProviders';
import { MAIN_CATEGORY_THEMES } from '../../utils/categoryThemes';
import { ActivityEditor } from '../activities/ActivityEditor';
import { ReasonPills } from './reasons';

interface SuggestionCardProps {
  activity: Activity;
  onStart: (activity: Activity) => void;
  /** Deze activiteit loopt nu als missie. */
  isRunning?: boolean;
  /** Korte redenen van de suggestie-engine (max. 2 getoond). */
  reasons?: string[];
}

const iconBtn =
  'grid size-11 shrink-0 place-items-center rounded-2xl transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current';
const menuItem =
  'flex min-h-11 w-full items-center gap-2.5 whitespace-nowrap rounded-xl px-3 text-left text-sm font-semibold text-slate-900 transition-colors hover:bg-black/5 focus-visible:bg-black/5 focus-visible:outline-none dark:text-slate-100 dark:hover:bg-white/10 dark:focus-visible:bg-white/10';

export function SuggestionCard({ activity, onStart, isRunning = false, reasons }: SuggestionCardProps) {
  const category = getMainCategory(activity.type, activity.energie, activity.categorie);
  const theme = MAIN_CATEGORY_THEMES[category];
  const CategoryIcon = theme.icon;
  const score = Math.max(0, Math.floor(Number(activity.score) || 0));
  const duur = Math.max(0, Math.round(Number(activity.duur) || 0));
  const isHighEnergy = activity.energie === 'Veel energie';
  const titleId = `activity-${activity.id}-title`;
  const menuId = useId();
  const { isFavorite, toggleFavorite, hide } = usePrefs();
  const { showToast } = useToast();
  const fav = isFavorite(activity.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Popover: klik buiten en Escape sluiten (Escape zet focus terug op de knop).
  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector<HTMLElement>('button')?.focus();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || menuBtnRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setMenuOpen(false);
      menuBtnRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [menuOpen]);

  const onFav = () => {
    toggleFavorite(activity.id);
    showToast(fav ? 'Verwijderd uit favorieten' : 'Toegevoegd aan favorieten');
  };

  const onHide = (duration: 'week' | 'always') => {
    setMenuOpen(false);
    hide(activity.id, duration);
    showToast(
      duration === 'week'
        ? `${activity.naam} een week verborgen. Terugzetten kan via het menu.`
        : `${activity.naam} verborgen. Terugzetten kan via het menu.`,
      4500,
    );
  };

  return (
    <article
      aria-labelledby={titleId}
      className={`rounded-3xl p-5 backdrop-blur-md transition-colors ${theme.cardBg} ${theme.textColor}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5 pt-2">
          <Pill icon={CategoryIcon} className={`${theme.pillBg} ${theme.accent}`}>
            {theme.label}
          </Pill>
          {score > 0 ? (
            <Pill icon={Medal} className={`${theme.timerBoxBg} ${theme.accent}`} title={`${score} keer gedaan`}>
              {score}× gedaan
            </Pill>
          ) : null}
        </div>
        <div className="-mr-2 -mt-2 flex shrink-0 items-center">
          <button
            type="button"
            onClick={onFav}
            aria-pressed={fav}
            aria-label={fav ? `Geen favoriet meer: ${activity.naam}` : `Favoriet: ${activity.naam}`}
            className={`${iconBtn} hover:bg-black/5 dark:hover:bg-white/10`}
          >
            <Heart size={20} strokeWidth={2.2} aria-hidden="true" className={fav ? 'fill-current' : ''} />
          </button>
          <div className="relative">
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? menuId : undefined}
              aria-label={`Meer opties: ${activity.naam}`}
              className={`${iconBtn} hover:bg-black/5 dark:hover:bg-white/10`}
            >
              <MoreVertical size={20} strokeWidth={2.2} aria-hidden="true" />
            </button>
            {menuOpen ? (
              <div
                ref={menuRef}
                id={menuId}
                role="menu"
                aria-label={`Opties voor ${activity.naam}`}
                className="absolute right-0 top-12 z-20 w-64 rounded-2xl bg-slate-50 p-1.5 text-slate-900 shadow-xl dark:border dark:border-white/10 dark:bg-slate-800"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setEditing(true);
                  }}
                  className={menuItem}
                >
                  <Pencil size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                  Bewerken
                </button>
                <button type="button" role="menuitem" onClick={() => onHide('week')} className={menuItem}>
                  <CalendarX size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                  Een week niet meer tonen
                </button>
                <button type="button" role="menuitem" onClick={() => onHide('always')} className={menuItem}>
                  <EyeOff size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                  Nooit meer tonen
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <h3 id={titleId} className="mt-2 text-lg font-bold leading-snug">
        {activity.naam}
      </h3>
      {activity.omschrijving ? <p className="mt-1 text-sm leading-relaxed opacity-85">{activity.omschrijving}</p> : null}

      {reasons && reasons.length > 0 ? (
        <div className="mt-3">
          <ReasonPills reasons={reasons} className={`${theme.pillBg} ${theme.accent}`} />
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <ul className="flex min-w-0 flex-wrap gap-1.5" aria-label="Kenmerken">
          <li>
            <Pill icon={Clock} className={theme.timerBoxBg}>
              {duur} min
            </Pill>
          </li>
          {activity.aantalPersonen ? (
            <li>
              <Pill icon={Users} className={theme.timerBoxBg} title={`Aantal spelers: ${activity.aantalPersonen}`}>
                {activity.aantalPersonen} pers.
              </Pill>
            </li>
          ) : null}
          {activity.locatie ? (
            <li>
              <Pill icon={MapPin} className={theme.timerBoxBg}>
                Op kaart
              </Pill>
            </li>
          ) : null}
          {activity.energie ? (
            <li>
              <Pill icon={isHighEnergy ? Zap : Sofa} className={theme.timerBoxBg}>
                {activity.energie}
              </Pill>
            </li>
          ) : null}
        </ul>

        <button
          type="button"
          onClick={() => onStart(activity)}
          aria-label={isRunning ? `${activity.naam} opnieuw starten` : `Start ${activity.naam}`}
          className={`ml-auto inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-2xl px-5 text-sm font-bold transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current ${theme.buttonBg}`}
        >
          <Play size={16} strokeWidth={2.4} aria-hidden="true" className="fill-current" />
          {isRunning ? 'Opnieuw' : 'Start'}
        </button>
      </div>
      {editing && <ActivityEditor activity={activity} onClose={() => setEditing(false)} />}
    </article>
  );
}
